import { doc, getDoc, DocumentData } from 'firebase/firestore';
import { db } from '@config/firebase';
import { EnhancedUser, PersonalInfo } from '../types/profile.types';
import { cacheManager, CACHE_KEYS, CACHE_TTL } from '@utils/cacheManager';
import { getAvatarUrlAsync } from '@utils/avatarUtils';

// Sentinel value for negative caching (caching "not found" results)
const CACHE_NOT_FOUND = '__CACHE_NOT_FOUND__';

class UserDataService {

/**
 * Fetch all users for a specific tenant
 * @param tenantId - The tenant ID to fetch users for
 * @returns Promise<EnhancedUser[]>
 */
  async fetchUsersForTenant(tenantId: string): Promise<EnhancedUser[]> {
    try {
      const cacheKey = `${CACHE_KEYS.TENANT_USERS}_${tenantId}`;
      const cachedUsers = cacheManager.get<EnhancedUser[]>(cacheKey);

      if (cachedUsers) {
        await this.prefetchAvatars(cachedUsers);
        return cachedUsers;
      }
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));

      if (!tenantDoc.exists()) {
        console.error('Tenant not found:', tenantId);
        return [];
      }

      const tenantData = tenantDoc.data();
      const userIds: string[] = tenantData.users || [];

      if (userIds.length === 0) {
        return [];
      }

      const enhancedUsers = await Promise.all(
        userIds.map(async (uid) => {
          const results = await Promise.allSettled([
            this.fetchUserProfile(uid),
            this.fetchUserAuthData(uid),
            this.fetchUserPrivateData(uid)
          ]);

          const profile = results[0].status === 'fulfilled' ? results[0].value : null;
          const authData = results[1].status === 'fulfilled' ? results[1].value : null;
          const privateData = results[2].status === 'fulfilled' ? results[2].value : null;
          return this.createEnhancedUser(uid, profile, authData, privateData);
        })
      );

      const validUsers = enhancedUsers.filter(user => user !== null) as EnhancedUser[];

      cacheManager.set(cacheKey, validUsers, CACHE_TTL.MEDIUM);

      await this.prefetchAvatars(validUsers);

      return validUsers;

    } catch (error) {
      console.error('Error fetching users for tenant:', error);
      return [];
    }
  }

/**
 * Fetch user profile from profiles collection
 * @param uid - User ID
 * @returns Promise<PersonalInfo | null>
 */
  async fetchUserProfile(uid: string): Promise<PersonalInfo | null> {
    try {
      const cacheKey = `${CACHE_KEYS.USER_PROFILE}_${uid}`;
      const cached = cacheManager.get<PersonalInfo | string>(cacheKey);

      if (cached && cached !== CACHE_NOT_FOUND) {
        return cached as PersonalInfo;
      }

      if (cached === CACHE_NOT_FOUND) {
        return null;
      }

      const profileDoc = await getDoc(doc(db, 'profiles', uid));

      if (!profileDoc.exists()) {
        cacheManager.set(cacheKey, CACHE_NOT_FOUND, CACHE_TTL.MEDIUM);
        return null;
      }

      const data = profileDoc.data() as DocumentData;

      const profile: PersonalInfo = {
        id: uid,
        email: data.email || '',
        profileImageUrl: data.profileImageUrl || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        quote: data.quote || '',
        professional: data.professional || false,
        mentor: data.mentor || false,
        locationData: data.locationData,
        educations: data.educations || [],
        works: data.works || [],
        activities: data.activities || [],
        questionResponses: data.questionResponses || [],
        interests: data.interests || [],
        links: data.links || [],
        contacts: data.contacts || [],
        rejectedProfiles: data.rejectedProfiles || [],
        matches: data.matches || [],
        lastActive: data.lastActive?.toDate(),
        createdAt: data.createdAt?.toDate(),
        meetings: data.meetings || [],
        referral_code: data.referral_code,
        affiliationInfo: data.affilationInfo || [], // Note: matches Swift typo "affilationInfo"
        userObjectives: data.userObjectives || [],
        userGoals: data.userGoals || [],
        userSources: data.userSources || [],
        userStreaks: data.userStreaks || [],
        prefAIChat: data.prefAIChat || [],
        prefMentorshipChat: data.prefMentorshipChat || [],
        acceptedCards: data.acceptedCards || [],
        rejectedCards: data.rejectedCards || [],
        customPreferences: data.customPreferences || {}
      };

      cacheManager.set(cacheKey, profile, CACHE_TTL.MEDIUM);

      return profile;

    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

/**
 * Fetch Firebase Auth data for a user using cloud function
 * @param uid - User ID
 * @returns Promise<any>
 */
  async fetchUserAuthData(uid: string): Promise<any> {
    // TODO: Implement getUserAuthData cloud function
    // For now, return null since the cloud function doesn't exist yet
    return null;
  }

/**
 * Fetch private user data from private_user_data collection
 * @param uid - User ID
 * @returns Promise<any>
 */
  async fetchUserPrivateData(uid: string): Promise<any> {
    const cacheKey = `${CACHE_KEYS.USER_PRIVATE_DATA}_${uid}`;
    try {
      const cached = cacheManager.get<any>(cacheKey);

      if (cached && cached !== CACHE_NOT_FOUND) {
        return cached;
      }

      if (cached === CACHE_NOT_FOUND) {
        return null;
      }

      const privateDataDoc = await getDoc(doc(db, 'private_user_data', uid));

      if (!privateDataDoc.exists()) {
        cacheManager.set(cacheKey, CACHE_NOT_FOUND, CACHE_TTL.MEDIUM);
        return null;
      }

      const privateData = privateDataDoc.data();

      cacheManager.set(cacheKey, privateData, CACHE_TTL.MEDIUM);

      return privateData;
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        cacheManager.set(cacheKey, CACHE_NOT_FOUND, CACHE_TTL.MEDIUM);
        return null;
      } else {
        console.error('Error fetching private user data:', error);
        return null;
      }
    }
  }
/**
 * Create enhanced user object combining auth and profile data
 * @param uid - User ID
 * @param profile - Profile data
 * @param authData - Firebase Auth data
 * @param privateData - Private user data
 * @returns EnhancedUser
 */
  private createEnhancedUser(uid: string, profile: PersonalInfo | null, authData: any, privateData: any): EnhancedUser {
    const displayName = profile
      ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email
      : authData?.email || 'Unknown User';

    // Determine last active date with fallbacks
    let lastActiveDate: Date | null = null;
    let lastActiveFormatted: string | undefined = undefined;

    if (privateData?.utc_last_active) {
      // Use private data if available
      lastActiveDate = new Date(privateData.utc_last_active * 1000);
      lastActiveFormatted = this.formatLastActive(lastActiveDate);
    } else if (profile?.lastActive) {
      // Fallback to profile data
      lastActiveDate = profile.lastActive;
      lastActiveFormatted = this.formatLastActive(lastActiveDate);
    } else if (profile?.createdAt) {
      // Further fallback to creation date with a note
      lastActiveFormatted = `Created ${this.formatLastActive(profile.createdAt)}`;
    } else {
      // Final fallback placeholder
      lastActiveFormatted = 'No activity data';
    }

    // Determine status with better fallbacks
    const status: 'active' | 'inactive' | 'disabled' =
      authData?.disabled ? 'disabled' :
        lastActiveDate && this.isRecentlyActive(lastActiveDate) ? 'active' :
          privateData === null ? 'inactive' : // If private data access failed, assume inactive
            'inactive';

    return {
      uid,
      authEmail: authData?.email,
      emailVerified: authData?.emailVerified,
      disabled: authData?.disabled,
      customClaims: authData?.customClaims,
      creationTime: authData?.metadata?.creationTime,
      lastSignInTime: authData?.metadata?.lastSignInTime,
      profile,
      displayName,
      status,
      lastActiveFormatted,
      privateData
    };
  }

/**
 * Fetch a single user by UID
 * @param uid - User ID
 * @returns Promise<EnhancedUser | null>
 */
  async fetchSingleUser(uid: string): Promise<EnhancedUser | null> {
    try {
      const results = await Promise.allSettled([
        this.fetchUserProfile(uid),
        this.fetchUserAuthData(uid),
        this.fetchUserPrivateData(uid)
      ]);

      const profile = results[0].status === 'fulfilled' ? results[0].value : null;
      const authData = results[1].status === 'fulfilled' ? results[1].value : null;
      const privateData = results[2].status === 'fulfilled' ? results[2].value : null;

      return this.createEnhancedUser(uid, profile, authData, privateData);
    } catch (error) {
      console.error('Error fetching single user:', error);
      return null;
    }
  }

/**
 * Check if user was recently active (within last 30 days)
 * @param lastActive - Last active date
 * @returns boolean
 */
  private isRecentlyActive(lastActive: Date): boolean {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastActive > thirtyDaysAgo;
  }

/**
 * Format last active date for display
 * @param lastActive - Last active date
 * @returns string
 */
  private formatLastActive(lastActive: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - lastActive.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    }
  }

  private async prefetchAvatars(users: EnhancedUser[]): Promise<void> {
    await Promise.all(
      users.map(user =>
        getAvatarUrlAsync(user.uid).catch((): string | null => {
          return null;
        })
      )
    );
  }

  invalidateUserCache(uid: string): void {
    const profileKey = `${CACHE_KEYS.USER_PROFILE}_${uid}`;
    const privateDataKey = `${CACHE_KEYS.USER_PRIVATE_DATA}_${uid}`;

    cacheManager.clear(profileKey);
    cacheManager.clear(privateDataKey);
  }

  invalidateTenantUsersCache(tenantId: string): void {
    const cacheKey = `${CACHE_KEYS.TENANT_USERS}_${tenantId}`;
    cacheManager.clear(cacheKey);
  }

  invalidateAllUserCache(tenantId: string, userIds?: string[]): void {
    this.invalidateTenantUsersCache(tenantId);

    if (userIds && userIds.length > 0) {
      userIds.forEach(uid => this.invalidateUserCache(uid));
    }
  }
}

// Create singleton instance
export const userDataService = new UserDataService();
export default userDataService;